import React, { useMemo, useState } from "react";
import {
    Box,
    Table,
    TableHeader,
    TableRow,
    TableCell,
    TableBody,
    Text,
    Button,
    Layer,
    Heading,
    Tabs,
    Tab,
    CheckBoxGroup,
    TextArea,
} from "grommet";
import { StatusGood, User, FormEdit, Close } from "grommet-icons";
import { Notification, Tip } from "grommet";
import axios from "axios";

const FREE_EMAIL_ORGS = new Set([
    "gmail",
    "googlemail",
    "yahoo",
    "ymail",
    "hotmail",
    "outlook",
    "live",
    "msn",
    "protonmail",
    "pm",
    "icloud",
    "me",
    "aol",
    "gmx",
    "zoho",
    "mail",
    "mailfence",
    "yandex",
    "fastmail",
    "hey",
    "duck",
]);

const userTypeMap = {
    1: "Manager",
    2: "Invited User",
    0: "Self Registered User",
};

const toTitle = (str = "") =>
    str
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.toUpperCase(0))
        .join(" ");

const normalizeOrg = (raw = "") => {
    const cleaned = String(raw).replace(/[^a-zA-Z0-9]+/g, " ").trim();
    if (!cleaned) return "N/A";
    const lower = cleaned.toLowerCase();
    if (FREE_EMAIL_ORGS.has(lower)) return "Individual Account";
    return toTitle(cleaned);
};

// Derive an organisation name from the email domain (drops common TLD/ccTLD suffixes)
export const getOrganisationFromEmail = (email = "") => {
    if (!email) return "N/A";
    const [, domainPart = ""] = String(email).split("@");
    if (!domainPart) return "N/A";

    const parts = domainPart.split(".").filter(Boolean);
    if (parts.length === 0) return "N/A";

    let base = "";
    if (
        parts.length >= 3 &&
        parts[parts.length - 1].length === 2 &&
        ["co", "com", "net", "org"].includes(parts[parts.length - 2])
    ) {
        base = parts[parts.length - 3];
    } else if (parts.length >= 2) {
        base = parts[parts.length - 2];
    } else {
        base = parts[0];
    }
    // Captialise the entire base properly
    // base = base.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");

    return normalizeOrg(base);
};

const AssessmentTable = ({ assessmentData = [] }) => {
    const [selected, setSelected] = useState(null);
    const [showDetailsLayer, setShowDetailsLayer] = useState(false);
    const [showUMLayer, setShowUMLayer] = useState(false);
    const [umActiveIndex, setUmActiveIndex] = useState(0);
    const [toastMessage, setToastMessage] = useState(null);
    const [updatingDnc, setUpdatingDnc] = useState(false);
    const [commentOpen, setCommentOpen] = useState(false);
    const [commentDraft, setCommentDraft] = useState("");
    const [commentText, setCommentText] = useState("");
    const [localUpdates, setLocalUpdates] = useState({});

    const applyLocalUpdate = (assesment_id, patch) =>
        setLocalUpdates(prev => ({
            ...prev,
            [assesment_id]: { ...(prev[assesment_id] || {}), ...patch },
        }));


    const onOpenAssessmentDetails = (assessment) => {
        setSelected(assessment);
        setShowDetailsLayer(true);
    };
    const onCloseAssessmentDetails = () => {
        setShowDetailsLayer(false);
        setSelected(null);
    };


    const onOpenRecommendations = (assessment) => {
        window.open(`/recommendations/${assessment.user_id}/${assessment.assesment_id}`, "_blank");
    };
    const markUserAsContacted = async (user) => {
        try {
            await axios.post("/api/pursuit/mark-user-as-contacted", {
                user_id: user.user_id,
            });
            const patch = { user_is_communicated: 1 };
            setSelected(prev => ({ ...prev, ...patch }));
            applyLocalUpdate(user.assesment_id, patch);
            setToastMessage(`${user.user_name || user.user_email} has been marked as contacted.`);
        } catch (err) {
            setToastMessage(`Failed to mark ${user.user_name || user.user_email} as contacted.`);
        }
    };

    const handleToggleDoNotContact = async (checked) => {
        setUpdatingDnc(true);
        try {
            const agreeToCommunicate = checked ? 0 : 1;
            await axios.put("/api/pursuit/agree-to-communicate", {
                user_id: selected.user_id,
                agree_to_communicate: agreeToCommunicate,
            });
            const patch = {
                user_agree_to_communicate: agreeToCommunicate,
                user_is_communicated: checked ? 0 : selected.user_is_communicated,
            };
            setSelected(prev => ({ ...prev, ...patch, user_do_not_contact: checked ? 1 : 0 }));
            applyLocalUpdate(selected.assesment_id, patch);
            setToastMessage(checked ? "User added to Do Not Contact list." : "User removed from Do Not Contact list.");
        } catch (err) {
            setToastMessage("Failed to update Do Not Contact status.");
        } finally {
            setUpdatingDnc(false);
        }
    };



    const sortedAssessments = useMemo(
        () =>
            [...assessmentData]
                .map(a => {
                    const patch = localUpdates[a.assesment_id];
                    return patch ? { ...a, ...patch } : a;
                })
                .sort((a, b) => a.user_is_communicated - b.user_is_communicated),
        [assessmentData, localUpdates]
    );


    const onOpenUMDetails = (assessment) => {
        setSelected(assessment);
        setUmActiveIndex(0);
        setShowUMLayer(true);
        setCommentText(assessment.comment || "");

    };
    const onCloseUMDetails = () => {
        setShowUMLayer(false);
        setSelected(null);
    };


    const hasComment = !!commentText;

    const openCommentModal = () => {
        setCommentDraft(commentText);
        setCommentOpen(true);
    };

    const handleSaveComment = async () => {
        try {
            await axios.put(`/api/pursuit/${selected.user_id}/comment`, {
                comment: commentDraft.trim(),
            });
            const patch = { comment: commentDraft.trim() };
            setSelected(prev => ({ ...prev, ...patch }));
            setCommentText(commentDraft.trim());
            applyLocalUpdate(selected.assesment_id, patch);
            setToastMessage("Comment saved.");
            setCommentOpen(false);
        } catch (e) {
            setToastMessage("Failed to save comment.");
        }
    };

    const truncateWords = (str = "", n = 12) => {
        const s = String(str || "").trim();
        if (!s) return "";
        const words = s.split(/\s+/);
        return words.length <= n ? s : words.slice(0, n).join(" ") + "…";
    };

    return (
        <>
            <Box overflow="auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell><Text weight="bold">#</Text></TableCell>
                            <TableCell>
                                <Text weight="bold">User Contactable</Text>
                            </TableCell>
                            <TableCell><Text weight="bold">Date</Text></TableCell>
                            <TableCell><Text weight="bold">User Name</Text></TableCell>
                            <TableCell><Text weight="bold">Organisation</Text></TableCell>
                            <TableCell><Text weight="bold">User Country</Text></TableCell>
                            <TableCell><Text weight="bold">User Type</Text></TableCell>
                            <TableCell><Text weight="bold">Comment</Text></TableCell>
                            <TableCell><Text weight="bold">Contacted on</Text></TableCell>
                            <TableCell align="center"><Text weight="bold">Actions</Text></TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedAssessments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    No assessments found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedAssessments.map((assessment, idx) => (
                                <TableRow key={assessment.assesment_id}>
                                    <TableCell><Text>{idx + 1}</Text></TableCell>
                                    <TableCell alignContent="center" alignSelf="center">
                                        <Text color={assessment.user_agree_to_communicate ? "brand" : "status-critical"}>
                                            {assessment.user_agree_to_communicate === 1 ? "Yes" : "No"}
                                        </Text>
                                    </TableCell>

                                    <TableCell>
                                        <Text>{new Date(assessment.end_date).toLocaleDateString()}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {!assessment.user_name || assessment.user_name === "unknown"
                                                ? assessment.user_email
                                                : assessment.user_name}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>{getOrganisationFromEmail(assessment.user_email)}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>{assessment.user_country}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {userTypeMap[assessment.user_type] || assessment.user_type}
                                        </Text>
                                    </TableCell>

                                    <TableCell>
                                        {(() => {
                                            const c = (userTypeMap[assessment.comment] ?? assessment.comment) || "";
                                            const normalized = c.trim();
                                            const isNA = !normalized || normalized === "N/A";

                                            return isNA ? (
                                                <Text>N/A</Text>
                                            ) : (
                                                <Tip
                                                    content={
                                                        <Box pad="xsmall" width={{ max: "medium" }}>
                                                            <Text size="small">{normalized}</Text>
                                                        </Box>
                                                    }
                                                    dropProps={{ align: { top: "bottom" } }}
                                                >
                                                    <Text>{truncateWords(normalized, 4)}</Text>
                                                </Tip>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        {assessment.user_communicated_at_date && assessment.user_communicated_at_time ? (
                                            <Text color="brand">
                                                {new Intl.DateTimeFormat("en-US", {
                                                    dateStyle: "medium",
                                                }).format(new Date(`${assessment.user_communicated_at_date}T${assessment.user_communicated_at_time}`))}{" "}
                                                at{" "}
                                                {new Intl.DateTimeFormat("en-US", {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                }).format(new Date(`1970-01-01T${assessment.user_communicated_at_time}`))}
                                            </Text>
                                        ) : (
                                            <Text color="status-critical">User not contacted</Text>
                                        )}
                                    </TableCell>


                                    <TableCell align="center">
                                        <Box direction="row" justify="center">
                                            {/* <Button
                                                icon={<View />}
                                                tip="Assessment Details"
                                                onClick={() => onOpenAssessmentDetails(assessment)}
                                                size="medium"
                                                color="brand"
                                            /> */}
                                            <Button
                                                icon={<User />}
                                                tip="User Details"
                                                onClick={() => onOpenUMDetails(assessment)}
                                                size="medium"
                                                color="brand"
                                            />
                                            <Button
                                                icon={<StatusGood />}
                                                tip="Recommendations"
                                                onClick={() => onOpenRecommendations(assessment)}
                                                size="medium"
                                                color="brand"
                                            />
                                        </Box>
                                    </TableCell>

                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>

            {/* Assessment Details Layer */}
            {showDetailsLayer && selected && (
                <Layer onEsc={onCloseAssessmentDetails} onClickOutside={onCloseAssessmentDetails}>
                    <Box pad="medium" width="large" gap="small">
                        <Heading level={3} margin="none" alignSelf="center" align="center">Assessment Details</Heading>
                        <Text>
                            <strong>User:</strong>{" "}
                            {!selected.user_name || selected.user_name === "unknown"
                                ? selected.user_email
                                : selected.user_name}{" "}
                            ({userTypeMap[selected.user_type] || selected.user_type})
                        </Text>
                        <Text><strong>Date:</strong> {new Date(selected.end_date).toLocaleString()}</Text>
                        <Text><strong>Total Topics:</strong> {selected.total_topic_count}</Text>
                        <Text><strong>Total Questions:</strong> {selected.query_count}</Text>
                        <Text><strong>Total Correct:</strong> {selected.incorrect_count}</Text>
                        <Box>
                            <Text weight="bold" margin={{ bottom: "xsmall" }}>Topics:</Text>
                            <Box pad={{ left: "small" }} gap="xsmall">
                                {[
                                    selected.topic1_name,
                                    selected.topic2_name,
                                    selected.topic3_name,
                                    selected.topic4_name,
                                    selected.topic5_name,
                                ]
                                    .filter(Boolean)
                                    .map((topic, i) => (
                                        <Text key={i}>{i + 1}. {topic}</Text>
                                    ))}
                            </Box>
                        </Box>
                        <Box direction="row" justify="between" margin={{ top: "medium" }}>
                            <Button
                                secondary
                                label="Recommendations"
                                onClick={() => onOpenRecommendations(selected)}
                                size="medium"
                            />
                            <Button label="Close" onClick={onCloseAssessmentDetails} size="medium" />
                        </Box>
                    </Box>
                </Layer>
            )}

            {/* User/Manager Details Layer */}
            {showUMLayer && selected && (
                <Layer
                    onEsc={onCloseUMDetails}
                    onClickOutside={onCloseUMDetails}
                    modal
                    responsive={false}
                    position="center"   // always center
                >
                    <Box
                        pad="medium"
                        width="large"
                        height={{ max: "70vh" }}
                        overflow={{ vertical: "auto" }}
                        gap="medium"
                        margin="none"
                    >
                        <Box direction="row" justify="between" align="start" margin={{ bottom: "small" }} gap="medium">
                            <Box flex>
                                <Tabs activeIndex={umActiveIndex} onActive={i => setUmActiveIndex(i)} margin={{ top: "none" }}>
                                    <Tab title="User Details">
                                        <Box gap="small" pad="small">
                                    <Text><strong>Name:</strong> {selected.user_name || selected.user_email}</Text>
                                    <Text><strong>Email:</strong> {selected.user_email}</Text>
                                    <Text><strong>Country:</strong> {selected.user_country}</Text>

                                    {/* Agreed + DNC inline */}
                                    <Box direction="row" align="center" gap="medium" wrap>
                                        <Text>
                                            <strong>Agreed to Communicate:</strong>{" "}
                                            <Text
                                                as="span"
                                                color={selected.user_agree_to_communicate === 1 ? "status-ok" : "status-critical"}
                                            >
                                                {selected.user_agree_to_communicate === 1 ? "YES" : "NO"}
                                            </Text>
                                        </Text>

                                        <CheckBoxGroup
                                            direction="row"
                                            options={[{ label: <Text>User asked not to contact</Text>, value: "dnc", reverse: true }]}
                                            value={selected.user_agree_to_communicate === 0 ? ["dnc"] : []}
                                            onChange={({ value: next }) => handleToggleDoNotContact(next.includes("dnc"))}
                                            disabled={updatingDnc}
                                            gap="xsmall"
                                        />
                                    </Box>

                                    {/* Buttons on one line, tighter spacing */}
                                    {!selected.manager_email && (
                                        <Box direction="row" gap="small" align="center" margin={{ top: "small" }}>
                                            <Button
                                                label="Email User"
                                                onClick={() => window.open(`mailto:${selected.user_email}`)}
                                                primary
                                                size="small"
                                                disabled={selected.user_agree_to_communicate !== 1}
                                            />
                                            <Button
                                                label="Mark User as Contacted"
                                                onClick={() => markUserAsContacted(selected)}
                                                primary
                                                size="small"
                                                disabled={selected.user_is_communicated === 1}
                                            />
                                        </Box>
                                    )}

                                    {/* Comment section */}
                                    <Box margin={{ top: "medium" }} gap="xsmall">
                                        {!hasComment ? (
                                            <Box direction="row" align="center" justify="between">
                                                <Text weight="bold">Comment</Text>
                                                <Button label="Add comment" onClick={() => { setCommentDraft(""); setCommentOpen(true); }} secondary size="small" />
                                            </Box>
                                        ) : (
                                            <>
                                                <Box direction="row" align="center" justify="between">
                                                    <Box direction="row" align="center" gap="xsmall">
                                                        <Text weight="bold">Comment</Text>
                                                        {/* Edit icon next to heading */}
                                                        <Button
                                                            icon={<FormEdit />}
                                                            onClick={() => { setCommentDraft(commentText); setCommentOpen(true); }}
                                                            plain
                                                            tip="Edit comment"
                                                        />
                                                    </Box>
                                                </Box>
                                                <Text>{commentText}</Text>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Tab>

                            <Tab title="Manager Details">
                                <Box pad="small">
                                    {selected.managers.length > 0 ? (
                                        <Box direction="column" gap="medium">
                                            {selected.managers.map((mgr, i) => (
                                                <Box
                                                    key={i}
                                                    pad="small"
                                                    border={i < selected.managers.length - 1 ? { side: "bottom", color: "light-4" } : undefined}
                                                >
                                                    <Box direction="row" justify="between" align="center">
                                                        <Box>
                                                            <Text margin={{ bottom: "xsmall" }}><strong>Name:</strong> {mgr.manager_name}</Text>
                                                            <Text margin={{ bottom: "xsmall" }}><strong>Email:</strong> {mgr.manager_email}</Text>
                                                            <Text><strong>Country:</strong> {mgr.manager_country}</Text>
                                                        </Box>

                                                        <Box direction="row" align="center" gap="small">
                                                            <Button
                                                                label="Email Manager"
                                                                onClick={() => window.open(`mailto:${mgr.manager_email}`)}
                                                                primary
                                                                size="small"
                                                                disabled={selected.user_agree_to_communicate !== 1}
                                                            />
                                                            {/* <Button
                                                                label="Mark User as Contacted"
                                                                onClick={() => markUserAsContacted(selected)}
                                                                color="status-ok"
                                                                secondary
                                                                size="small"
                                                                disabled={selected.user_is_communicated === 1}
                                                            /> */}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Text>No manager for this user.</Text>
                                    )}
                                </Box>
                            </Tab>
                        </Tabs>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={onCloseUMDetails}
                                plain
                                hoverIndicator
                                alignSelf="start"
                            />
                        </Box>
                    </Box>

                    {toastMessage && (
                        <Notification
                            toast
                            status="normal"
                            title="User Update"
                            message={toastMessage}
                            onClose={() => setToastMessage(null)}
                        />
                    )}

                    {/* Small comment modal */}
                    {commentOpen && (
                        <Layer
                            onEsc={() => setCommentOpen(false)}
                            onClickOutside={() => setCommentOpen(false)}
                            modal
                            responsive={false}
                            position="center"
                        >
                            <Box pad="medium" width="medium" gap="small">
                                <Text weight="bold">Comment</Text>
                                <TextArea rows={6} value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} />
                                <Box direction="row" justify="end" gap="small" margin={{ top: "small" }}>
                                    <Button label="Cancel" onClick={() => setCommentOpen(false)} secondary size="small" />
                                    <Button label="Save" onClick={handleSaveComment} primary size="small" disabled={!commentDraft.trim()} />
                                </Box>
                            </Box>
                        </Layer>
                    )}
                </Layer>
            )}

        </>
    );
};

export default AssessmentTable;
